import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Trophy, ChevronRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { api } from "../lib/api";

/**
 * Compact "Friends brought" card with personal score + rank + leaderboard top-3.
 * Renders nothing if both score=0 AND no leaderboard entries (cold-start clean UI).
 */
export default function ReferralStatsCard() {
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState([]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/me/referrals").then((r) => r.data).catch(() => ({ total: 0, rank: null, items: [] })),
      api.get("/leaderboard/referrals?limit=5").then((r) => r.data).catch(() => []),
    ]).then(([mineData, boardData]) => {
      if (!mounted) return;
      setMe(mineData);
      setBoard(boardData);
    });
    return () => { mounted = false; };
  }, []);

  if (!me) return null;
  if (me.total === 0 && board.length === 0) return null;

  return (
    <div className="pp-card-elevated p-5" data-testid="referral-stats">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Friends brought</div>
          <div className="font-heading text-3xl font-black mt-1 flex items-center gap-2" data-testid="referral-score">
            <Sparkles size={20} className="text-[#00E676]" />
            {me.total}
          </div>
          {me.rank && (
            <div className="text-xs text-zinc-400 mt-1" data-testid="referral-rank">
              <Trophy size={11} className="inline -mt-0.5 me-1 text-[#FFD60A]" />
              Rank #{me.rank} on the leaderboard
            </div>
          )}
          {me.total === 0 && (
            <p className="text-xs text-zinc-500 mt-1">Share your event link to earn your first point.</p>
          )}
        </div>
        <Link to="/leaderboard" data-testid="referral-leaderboard-link"
          className="text-xs text-[#00E676] hover:underline flex items-center gap-1 shrink-0">
          Leaderboard <ChevronRight size={12} />
        </Link>
      </div>

      {board.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/5" data-testid="referral-mini-board">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Top {Math.min(3, board.length)} this season</div>
          <div className="space-y-2">
            {board.slice(0, 3).map((row) => (
              <div key={row.user.id} className="flex items-center gap-3" data-testid={`referral-board-${row.rank}`}>
                <span className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${row.rank === 1 ? "bg-[#FFD60A] text-black" : row.rank === 2 ? "bg-zinc-300 text-black" : row.rank === 3 ? "bg-[#cd7f32] text-black" : "bg-white/5 text-zinc-400"}`}>{row.rank}</span>
                <Avatar className="h-7 w-7"><AvatarImage src={row.user.avatar_url || ""} /><AvatarFallback className="bg-[#007AFF] text-white text-xs">{(row.user.name || "?").charAt(0)}</AvatarFallback></Avatar>
                <div className="text-sm flex-1 truncate">{row.user.name}</div>
                <div className="text-xs text-[#00E676] font-bold">{row.score}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
